import type { ShippingClient, TypedClient } from '@platform/openapi';
import type { TrackStepStatus } from '@platform/design-system';
import { canDispatchWith, useCarriers } from '../settings/carriers.ts';
import { useSenderProfile } from '../settings/sender.ts';
import { useBranding } from './branding.ts';

export const SETUP_STEP_IDS = ['welcome', 'store', 'brand', 'delivery', 'orders'] as const;
export type SetupStepId = (typeof SETUP_STEP_IDS)[number];

export const TRACKED_STEP_IDS = [
  'store',
  'brand',
  'delivery',
  'orders',
] as const satisfies readonly SetupStepId[];
type TrackedStepId = (typeof TRACKED_STEP_IDS)[number];

export const SKIPPABLE_STEP_IDS: readonly SetupStepId[] = ['brand', 'delivery'];

export const LAST_SETUP_STEP = SETUP_STEP_IDS.length - 1;

export type SetupFacts = {
  readonly branded: boolean;
  readonly dispatchableCarriers: number;
  readonly hasSender: boolean;
};

export type TrackedStep = { readonly id: TrackedStepId; readonly status: TrackStepStatus };

export const stepIndexOf = (id: SetupStepId): number => SETUP_STEP_IDS.indexOf(id);

export const setupStepAt = (index: number): SetupStepId => SETUP_STEP_IDS[index] ?? 'welcome';

const completion = (facts: SetupFacts): Readonly<Record<TrackedStepId, boolean>> => ({
  store: true,
  brand: facts.branded,
  delivery: facts.dispatchableCarriers > 0 && facts.hasSender,
  orders: true,
});

export const trackedStepsOf = (facts: SetupFacts): readonly TrackedStep[] => {
  const done = completion(facts);
  const next = TRACKED_STEP_IDS.find((id) => !done[id]);
  return TRACKED_STEP_IDS.map((id) => {
    if (done[id]) return { id, status: 'done' };
    return { id, status: id === next ? 'next' : 'todo' };
  });
};

export const resumeStepOf = (steps: readonly TrackedStep[]): SetupStepId =>
  steps.find((step) => step.status === 'next')?.id ?? 'welcome';

export type SetupProgress =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly error: unknown; readonly retry: () => void }
  | { readonly state: 'ready'; readonly steps: readonly TrackedStep[] };

export const useSetupProgress = (api: TypedClient, shipping: ShippingClient): SetupProgress => {
  const branding = useBranding(api);
  const carriers = useCarriers(shipping);
  const sender = useSenderProfile(shipping);
  const failed = [branding, carriers, sender].find((query) => query.isError);
  if (failed !== undefined)
    return {
      state: 'failed',
      error: failed.error,
      retry: () => {
        void failed.refetch();
      },
    };
  if (branding.data === undefined || carriers.data === undefined || sender.data === undefined)
    return { state: 'loading' };
  return {
    state: 'ready',
    steps: trackedStepsOf({
      branded: branding.data.colors !== null,
      dispatchableCarriers: carriers.data.items.filter(canDispatchWith).length,
      hasSender: sender.data.profile !== null,
    }),
  };
};
